// Rejestr ośrodka: wszystkie znaleziska i akcje ośrodka w jednej tabeli, edycja jak w Excelu, historia zmian,
// zbiorcza weryfikacja pozycji oznaczonych po migracji („do weryfikacji”).
import { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  createIssue, deleteIssue, downloadIssuesTemplate, downloadResortIssues, fetchResortIssues, parseIssuesSpreadsheet,
  reviewIssues, updateIssue, type SiteIssue,
} from "../../api/siteIssuesApi";
import { getTemplateProblems } from "../../api/questionsApi";
import { ActionStatus } from "../../types/reportActions";
import { Button } from "../../components/ui/button";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "../../components/ui/Modal";
import { cn } from "../../lib/utils";
import { IssueLogGrid, IssueLogSource, today } from "../report/actionLog/IssueLogGrid";
import { SITE_ISSUE_RULES } from "../report/actionLog/actionLogFormat";
import { ISSUE_FILTERS, IssueFilter, matchesFilter, rowFromIssue, stateFromRow } from "./siteIssueRows";
import { SiteIssueHistoryModal } from "./SiteIssueHistoryModal";

const HEADERS = { potentialSeriousFinding: "Poważne", deviationDescription: "Opis" };

export const SiteIssuesPage = () => {
  const { resortId } = useParams<{ resortId: string }>();
  const queryClient = useQueryClient();
  const queryKey = ["siteIssues", resortId];
  const { data, isLoading, error } = useQuery(queryKey, () => fetchResortIssues(resortId!), { enabled: !!resortId });
  const { data: dictionary } = useQuery(["problemsDictionary"], getTemplateProblems, { staleTime: 10 * 60 * 1000 });
  const categoryName = (id: number) => dictionary?.categories?.find((c: { id: number; value: string }) => c.id === id)?.value ?? String(id);

  // Filtr startowy z adresu (np. link z podglądu na stronie badania: ?filter=open)
  const [searchParams] = useSearchParams();
  const [filter, setFilter] = useState<IssueFilter>(() => {
    const f = searchParams.get("filter");
    return f && (ISSUE_FILTERS as readonly string[]).includes(f) ? (f as IssueFilter) : "all";
  });
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [historyOf, setHistoryOf] = useState<SiteIssue | null>(null);
  const [closing, setClosing] = useState(false);

  const items = useMemo(() => data?.items ?? [], [data]);
  const byId = useMemo(() => new Map(items.map((i) => [i.id, i])), [items]);
  const rows = useMemo(() => (data ? items.filter((i) => matchesFilter(i, filter)).map(rowFromIssue) : undefined), [data, items, filter]);

  const counts = {
    open: items.filter((i) => i.status === ActionStatus.Open).length,
    review: items.filter((i) => i.needsReview).length,
    serious: items.filter((i) => i.status === ActionStatus.Open && (i.serious || i.seriousBreach)).length,
    closed: items.filter((i) => i.status === ActionStatus.Closed).length,
  };

  const refresh = () => queryClient.invalidateQueries(queryKey);

  const source: IssueLogSource = {
    rows,
    isLoading,
    error,
    save: async (row) => {
      const saved = row.id ? byId.get(row.id) : undefined;
      const state = stateFromRow(row, saved);
      if (row.id) {
        await updateIssue(row.id, state);
        return row.id;
      }
      return createIssue(resortId!, state);
    },
    remove: (id) => deleteIssue(id),
    refresh,
    parseXlsx: parseIssuesSpreadsheet,
    downloadTemplate: downloadIssuesTemplate,
    downloadData: () => downloadResortIssues(resortId!),
  };

  const review = async (close: boolean, closedDate?: string, actionsTaken?: string) => {
    const ids = Array.from(selected);
    try {
      const res = await reviewIssues(ids, close, closedDate, actionsTaken);
      const failed = Object.entries(res.errors);
      if (res.updated) toast.success(close ? `Zamknięto pozycje: ${res.updated}` : `Potwierdzono otwarte: ${res.updated}`);
      if (failed.length) {
        const first = failed[0][1][0];
        toast.error(`Bez zmian: ${failed.length} (np. „${first}”)`);
      }
      setSelected(new Set(failed.map(([id]) => id)));
      refresh();
    } catch (e: any) {
      toast.error(`Nie zapisano: ${e?.message ?? "błąd serwera"}`);
    }
  };

  const FILTERS: { id: IssueFilter; label: string }[] = [
    { id: "all", label: `Wszystkie (${items.length})` },
    { id: "open", label: `Otwarte (${counts.open})` },
    { id: "review", label: `Do weryfikacji (${counts.review})` },
    { id: "serious", label: "Poważne / Serious Breach" },
    { id: "closed", label: `Zamknięte (${counts.closed})` },
  ];

  return (
    <div className="flex-1 overflow-y-auto bg-[var(--sc-bg)] px-4 md:px-12 py-9">
      <div className="mb-6">
        {data && (
          <Link to={`/study/${data.studyId}`} className="text-[12px] text-[var(--sc-blue)] hover:underline">
            ← {data.studyName}
          </Link>
        )}
        <h1 className="text-[28px] font-light tracking-tight text-[var(--sc-text)] leading-none mt-2">
          Rejestr ośrodka{data ? `: ${data.resortName}` : ""}
        </h1>
        <p className="text-[13px] text-[var(--sc-muted)] mt-1">
          Znaleziska i akcje ośrodka przez całe badanie. Zmiany tutaj i w raportach trafiają do historii pozycji.
        </p>
      </div>

      {counts.review > 0 && (
        <div className="mb-4 rounded-xl border border-[#F2D9A6] bg-[var(--sc-amber-bg)] px-4 py-3 text-[13px] text-[var(--sc-text)]">
          <strong>{counts.review}</strong> {counts.review === 1 ? "pozycja wymaga" : "pozycji wymaga"} weryfikacji: otwarte
          znaleziska, których nie przeniesiono do ostatniego raportu ośrodka, albo zamknięte bez daty. Zaznacz je i zamknij
          albo potwierdź, że są nadal otwarte.{" "}
          {filter !== "review" && (
            <button className="text-[var(--sc-blue)] hover:underline" onClick={() => setFilter("review")}>Pokaż je</button>
          )}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2 mb-4">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            onClick={() => { setFilter(f.id); setSelected(new Set()); }}
            className={cn(
              "text-[12px] px-3 py-1.5 rounded-full border transition-colors",
              filter === f.id
                ? "bg-[var(--sc-amber)] border-[var(--sc-amber)] text-[#0F0F0F] font-medium"
                : "bg-[var(--sc-surface)] border-[var(--sc-border)] text-[var(--sc-muted)] hover:text-[var(--sc-text)]"
            )}
          >
            {f.label}
          </button>
        ))}
        {counts.serious > 0 && (
          <span className="ml-auto text-[12px] text-[var(--sc-red)]">Otwarte poważne: {counts.serious}</span>
        )}
      </div>

      <IssueLogGrid
        source={source}
        title="Znaleziska i akcje"
        rules={SITE_ISSUE_RULES}
        headers={HEADERS}
        dataExport={{ label: "Rejestr ośrodka (.xlsx)", hint: "wszystkie pozycje do pracy w Excelu", fileName: "Rejestr_osrodka" }}
        selection={{ selected, onChange: setSelected }}
        renderRowActions={(row) => (
          <button
            type="button"
            className="text-[11px] text-[var(--sc-blue)] hover:underline px-1"
            onClick={() => setHistoryOf(byId.get(row.id!) ?? null)}
          >
            historia
          </button>
        )}
        toolbar={selected.size > 0 && (
          <div className="flex items-center gap-2 mr-2">
            <span className="text-[12px] text-[var(--sc-muted)]">Zaznaczono: {selected.size}</span>
            <Button size="sm" variant="secondary" onClick={() => setClosing(true)}>Zamknij…</Button>
            <Button size="sm" variant="secondary" onClick={() => review(false)}>Potwierdź, że otwarte</Button>
          </div>
        )}
      />

      {closing && (
        <CloseIssuesModal
          count={selected.size}
          onCancel={() => setClosing(false)}
          onConfirm={async (date, note) => { setClosing(false); await review(true, date, note); }}
        />
      )}

      {historyOf && (
        <SiteIssueHistoryModal
          issueId={historyOf.id}
          title={historyOf.description.slice(0, 60)}
          categoryName={categoryName}
          onClose={() => setHistoryOf(null)}
        />
      )}
    </div>
  );
};

const CloseIssuesModal = ({
  count, onCancel, onConfirm,
}: { count: number; onCancel: () => void; onConfirm: (date: string, note: string) => void }) => {
  const [date, setDate] = useState(today());
  const [note, setNote] = useState("");
  return (
    <Modal open onClose={onCancel} size="md">
      <ModalHeader title={`Zamknij zaznaczone (${count})`} onClose={onCancel} />
      <ModalBody>
        <label className="block text-[12px] text-[var(--sc-muted)] mb-1" htmlFor="close-date">Data zamknięcia</label>
        <input
          id="close-date"
          type="date"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          className="w-full rounded-lg border border-[var(--sc-border)] px-3 py-2 text-sm bg-[var(--sc-surface)] text-[var(--sc-text)]"
        />
        <label className="block text-[12px] text-[var(--sc-muted)] mt-4 mb-1" htmlFor="close-note">
          Podjęte działania (opcjonalnie, dopisane do każdej pozycji)
        </label>
        <textarea
          id="close-note"
          rows={3}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          className="w-full rounded-lg border border-[var(--sc-border)] px-3 py-2 text-sm bg-[var(--sc-surface)] text-[var(--sc-text)]"
        />
      </ModalBody>
      {/* własne przyciski: domyślny onSave pokazuje ogólny toast obok komunikatu z liczbą zamkniętych */}
      <ModalFooter
        right={
          <div className="flex gap-2">
            <Button size="sm" variant="secondary" onClick={onCancel}>Anuluj</Button>
            <Button size="sm" onClick={() => onConfirm(date, note)} disabled={!date}>Zamknij pozycje</Button>
          </div>
        }
      />
    </Modal>
  );
};
